import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Bar, Line, HorizontalBar, Pie } from 'react-chartjs-2'
import styles from './index.module.scss'
import { COLORS } from '../../../utilities/constants'
import { formatToUSD } from '../../../utilities/formatters'
import PercentOverdueGuage from '../percent-overdue'
import BarChartPagination from '../../bar-chart-pagination'

function ARChart ({
  type = 'bar',
  horizontal = false,
  series,
  categories,
  labels,
  height = 350,
  width = '100%',
  title = '',
  markerColor = COLORS?.PRIMARY,
  stacked = false,
  displayTotal = false,
  options = {},
  gaugeValue = 0,
  fakeCount = null,
  onPageRequest,
  pageInfo,
  totalCount
}) {
  const [highlightedIndex, setHighlightedIndex] = useState(null)

  const datasets = series.map((s) => {
    const colors = highlightedIndex !== null
      ? Array(s?.data?.length).fill((s.color || markerColor))
        .map((item, index) => index === highlightedIndex ? (s.color || markerColor) : `${s.color || markerColor}40`)
      : (s.color || markerColor)
    return {
      label: s.name || '',
      data: s.data || [],
      backgroundColor: colors,
      borderColor: colors,
      borderWidth: 2,
      fill: type === 'bar',
      tension: type === 'line' ? 0.2 : 0
    }
  })

  const randomColors = () => {
    const colorKeys = Object.keys(COLORS || {})
    return Array.from({ length: categories?.length || labels?.length }, (item, index) => {
      return COLORS[colorKeys?.at(index)] || `#${Math.floor(Math.random() * 16777215).toString(16)}`
    })
  }

  const data = {
    labels: categories,
    datasets
  }

  const colorsForPieDoughnut = randomColors()
  const colors = highlightedIndex !== null
    ? colorsForPieDoughnut
      .map((item, index) => index === highlightedIndex ? item : `${item}40`)
    : colorsForPieDoughnut

  const pieDoughnutDataset = {
    label: series[0]?.name || '',
    data: series[0]?.data || [],
    backgroundColor: options?.colors || colors
  }

  const pieDoughnutData = {
    labels: labels || categories,
    datasets: [pieDoughnutDataset]
  }

  const optionsForBarAndLine = {
    layout: {
      padding: {
        top: 5,
        bottom: 5,
        left: 10,
        right: 10
      }
    },
    onClick: (e, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index
        setHighlightedIndex(index === highlightedIndex ? null : index)
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom'
      },
      title: {
        display: false
      }
    },
    tooltips: {
      enabled: true,
      callbacks: {
        label: function (context) {
          const label = series[context.datasetIndex].name
          const val = context.value
          return `${label}: $${formatToUSD(parseFloat(val))}`
        }
      }
    },
    scales: {
      xAxes: [
        {
          stacked,
          ticks: {
            autoSkip: false,
            beginAtZero: true,
            maxRotation: 45,
            minRotation: 0,
            font: {
              size: 12
            },
            callback: function (value) {
              return formatToUSD(parseFloat(value))
            }
          }
        }
      ],
      yAxes: [
        {
          stacked,
          ticks: {
            callback: function (value) {
              return horizontal ? value : formatToUSD(parseFloat(value))
            }
          }
        }
      ]
    },
    ...options
  }

  const optionsForPieDoughnut = {
    layout: {
      padding: {
        top: 5,
        bottom: 5,
        left: 10,
        right: 10
      }
    },
    onClick: (e, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index
        setHighlightedIndex(index === highlightedIndex ? null : index)
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom'
      },
      title: {
        display: false
      }
    },
    tooltips: {
      enabled: true,
      callbacks: {
        label: function (context) {
          const val = pieDoughnutDataset?.data[context?.index]
          const data = pieDoughnutDataset.data
          const total = data.reduce((acc, cur) => acc + cur, 0)
          const percentage = total > 0 ? ((val / total) * 100).toFixed(2) : 0
          const label = (labels || categories)[context?.index] || ''
          return title === 'Invoice Status Tracker' ? `${label}: ${val} (${percentage}%)` : `${label}: $${formatToUSD(parseFloat(val))} (${percentage}%)`
        }
      }
    },
    ...options
  }

  const ChartComponentLoader = () => {
    switch (type) {
      case 'line':
        return <Line data={data} options={optionsForBarAndLine} />
      case 'pie':
        return <Pie data={pieDoughnutData} options={optionsForPieDoughnut} />
      case 'gauge':
        return <PercentOverdueGuage gaugeValue={gaugeValue} />
      case 'bar':
        if (horizontal) return <HorizontalBar data={data} options={optionsForBarAndLine} />
        return <Bar data={data} options={optionsForBarAndLine} />
      case 'column':
        if (horizontal) return <HorizontalBar data={data} options={optionsForBarAndLine} />
        return <Bar data={data} options={optionsForBarAndLine} />
      default:
        return <Bar data={data} options={optionsForBarAndLine} />
    }
  }

  return (
    <div className={styles.chartParent}>
      <div className={styles.chart} style={{ width, height }}>
        {displayTotal && series && series?.at(0)?.data?.length > 0 && (
          <p className={styles.subtitle}>
            Total: {series?.at(0)?.data?.reduce((acc = 0, a) => acc + a)}
          </p>
        )}
        <div
          className={styles.chartContainer}>
          {ChartComponentLoader()}
        </div>
        {
          type === 'column' &&
            <div className={styles.chartPagination}>
              <BarChartPagination
                pageInfo={pageInfo}
                totalCount={totalCount}
                onPageRequest={onPageRequest}
                fakeCount={fakeCount}
              />
            </div>
        }
        <p className={styles.title}>{title}</p>
      </div>
    </div>
  )
}

ARChart.propTypes = {
  type: PropTypes.string,
  title: PropTypes.string,
  markerColor: PropTypes.string,
  horizontal: PropTypes.bool,
  series: PropTypes.any.isRequired,
  categories: PropTypes.array,
  labels: PropTypes.array,
  height: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  stacked: PropTypes.bool,
  displayTotal: PropTypes.bool,
  options: PropTypes.object,
  gaugeValue: PropTypes.number,
  fakeCount: PropTypes.shape({
    start: PropTypes.number.isRequired,
    end: PropTypes.number.isRequired
  }),
  onPageRequest: PropTypes.func.isRequired,
  pageInfo: PropTypes.object,
  totalCount: PropTypes.number
}

export default ARChart
